import type { SceneStore } from '@os/doc';
import type { Api } from '../../lib/api';
import type { AssetImportHandler, ImportHandler } from '../editor/Editor';
import { BoardMedia } from './board-media';
import { importAssets, importImages, type CheckAsset, type ProcessImage } from './import';
import type { LocalMediaStore } from './store';
import { watchBoardThumbnail } from './thumbnail';
import { UploadQueue } from './uploads';

export type BoardMediaSession = {
  resolver: BoardMedia;
  queue: UploadQueue;
  onImport: ImportHandler;
  /** One image stored and uploaded as an asset of the board, placed nowhere (link previews); its id, or null. */
  importAsset(file: File): Promise<string | null>;
  /** One picture for "Replace image" (P5f): stored and uploaded like a drop, its id and size; or why it failed. */
  onImportAsset: AssetImportHandler;
  destroy(): void;
};

/**
 * Media of one open server board: the remote resolver, the upload queue (resumed from IndexedDB), the import
 * handler that feeds it and the board preview, regenerated on leave after local edits. A read-only board imports
 * nothing and never uploads a preview.
 */
export function createBoardMediaSession(o: {
  boardId: string;
  api: Pick<Api, 'checkAsset' | 'assetUrls' | 'putAssetVariant' | 'putBoardThumbnail'>;
  store: SceneStore;
  local: LocalMediaStore;
  reducedMotion?: boolean;
  process?: ProcessImage;
  onThumbnail?: () => void;
}): BoardMediaSession {
  const resolver = new BoardMedia({ boardId: o.boardId, api: o.api, local: o.local, reducedMotion: o.reducedMotion });
  const queue = new UploadQueue({ boardId: o.boardId, api: o.api, local: o.local, store: o.store });
  const unsubscribe = queue.subscribe(() => resolver.setUploading(queue.uploadingIds()));
  void queue.resumePending();

  const check: CheckAsset = async (sha256) => {
    const id = await o.api.checkAsset(o.boardId, sha256);
    if (!id) return null;
    const [asset] = await o.api.assetUrls(o.boardId, [id]);
    return asset ? { assetId: id, w: asset.width, h: asset.height } : null;
  };
  const opts = { process: o.process, check, onStored: (meta: { assetId: string }, name: string) => queue.enqueue(meta.assetId, name) };
  const onImport: ImportHandler = async (files, at, adopt) => {
    if (o.store.readOnly) return { failed: [] };
    return importImages(files, at, o.store, o.local, { ...opts, adopt });
  };
  const onImportAsset: AssetImportHandler = async (file) => {
    if (o.store.readOnly) return { asset: null, failed: [] };
    const { assets, failed } = await importAssets([file], o.local, opts);
    return { asset: assets[0] ?? null, failed };
  };
  const importAsset = async (file: File) => {
    if (o.store.readOnly) return null;
    return (await importAssets([file], o.local, opts)).assets[0]?.assetId ?? null;
  };

  // Preview sources: this device's 256 px blob, else the R2 variant through CORS (a tainted canvas cannot be exported).
  const load = async (assetId: string): Promise<ImageBitmap | null> => {
    const blob = o.local.blob(assetId, '256');
    if (blob) return createImageBitmap(blob);
    const url = resolver.urlFor(assetId, 256);
    if (!url) return null;
    const res = await fetch(url, { mode: 'cors' });
    return res.ok ? createImageBitmap(await res.blob()) : null;
  };
  const stopThumbnail = watchBoardThumbnail({ store: o.store, boardId: o.boardId, api: o.api, load, enabled: () => !o.store.readOnly, onUploaded: o.onThumbnail });

  return {
    resolver,
    queue,
    onImport,
    importAsset,
    onImportAsset,
    destroy() {
      stopThumbnail();
      unsubscribe();
      queue.destroy();
      resolver.destroy();
    },
  };
}
